/* eslint-disable react/prop-types */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect, forwardRef } from 'react';
import { isDesktop } from 'react-device-detect';

import ItemsGrid from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsGrid';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import useInView from '@wconcept/wconcept-ui-renewal/hooks/useInView';
import { useShortFormPlayback } from '@wconcept/wconcept-ui-renewal/hooks/useShortFormPlayback';
import * as S from './style';

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children?: React.ReactNode;
  perView?: number;
  xl?: { perView: number; rowGap: number; colGap: number };
  virtual?: boolean;
  /** virtual 모드 모바일 그리드 열 수. 미지정 시 2 */
  mobileVirtualColumnCount?: number;
  shortFormPlayback?: boolean;
  viewportOffset?: {
    top: number;
    bottom: number;
  };
  isVideoPlay?: boolean;
  style?: React.CSSProperties;
  gridRef?: React.RefObject<HTMLDivElement | null>;
  gridProps?:
    | {
        style: React.CSSProperties;
      }
    | undefined;
}

interface VideoChildProps {
  isActive?: boolean;
  itemRef?: React.RefObject<HTMLDivElement | null>;
  onVideoEnd?: () => void;
}

export interface ObservedItemProps {
  children: React.ReactElement<VideoChildProps>;
  shouldObserve: boolean;
  index: number;
  setActiveVideoIndex: (index: number | null) => void;
  activeVideoIndex: number | null;
  visibleIndices: number[];
  viewportOffset?: {
    top: number;
    bottom: number;
  };
  isVideoPlay?: boolean;
}

// ObservedItem 컴포넌트 메모이제이션
export const ObservedItem = memo(
  ({
    children,
    shouldObserve,
    index,
    setActiveVideoIndex,
    activeVideoIndex,
    visibleIndices,
    viewportOffset = { top: 0, bottom: 0 },
    isVideoPlay = true,
  }: ObservedItemProps) => {
    const [ref, isVisible] = useInView(
      {
        root: null,
        rootMargin: `-${viewportOffset.top}px 0px -${viewportOffset.bottom}px 0px`,
        threshold: 0.8,
      },
      shouldObserve
    );

    const { videoSrc, vid } = (children.props as any) || {};
    const hasVideo = !!(videoSrc || vid);

    const handleVideoEnd = useCallback(() => {
      const currentIdx = visibleIndices.indexOf(index);

      if (currentIdx === visibleIndices.length - 1) {
        setActiveVideoIndex(null);
      } else {
        setActiveVideoIndex(visibleIndices[currentIdx + 1]);
      }
    }, [index, setActiveVideoIndex, visibleIndices]);

    useEffect(() => {
      if (!hasVideo || !isVideoPlay) return;

      if (isVisible) {
        // 현재 인덱스가 visibleIndices에 없다면 추가
        if (!visibleIndices.includes(index)) {
          const insertPosition = visibleIndices.findIndex(i => i > index);
          if (insertPosition === -1) {
            visibleIndices.push(index);
          } else {
            visibleIndices.splice(insertPosition, 0, index);
          }

          // 첫 번째 보이는 비디오라면 활성화
          if (visibleIndices.length > 0 && activeVideoIndex === null) {
            // 약간의 지연을 두고 활성화 (DOM 업데이트 대기)
            setTimeout(() => {
              setActiveVideoIndex(visibleIndices[0]);
            }, 50);
          }
        }
      } else {
        // 보이지 않는다면 visibleIndices에서 제거
        const idx = visibleIndices.indexOf(index);
        if (idx > -1) {
          visibleIndices.splice(idx, 1);

          // 현재 활성화된 비디오가 보이지 않게 되었다면 다음 비디오 활성화
          if (index === activeVideoIndex) {
            const nextIndex =
              visibleIndices.length > 0 ? visibleIndices[0] : null;
            setActiveVideoIndex(nextIndex);
          }
        }
      }
    }, [
      isVisible,
      index,
      visibleIndices,
      activeVideoIndex,
      setActiveVideoIndex,
      hasVideo,
      isVideoPlay,
    ]);

    return React.cloneElement(children, {
      isActive: isVideoPlay && hasVideo ? index === activeVideoIndex : false,
      itemRef: ref,
      onVideoEnd: handleVideoEnd,
    });
  }
);

const ProductList = forwardRef<HTMLDivElement, IProps>(
  (
    {
      className,
      children,
      perView = 2,
      xl = { perView: 6, rowGap: 60, colGap: 18 },
      virtual = false,
      mobileVirtualColumnCount,
      shortFormPlayback = false,
      viewportOffset = { top: 0, bottom: 0 },
      isVideoPlay = true,
      style,
      gridRef,
      gridProps,
      ...rest
    },
    ref
  ) => {
    // virtual(LP)은 기기 감지와 무관하게 perView 클래스를 적용한다.
    // react-device-detect의 isDesktop이 true여도 모바일 UA/열 토글이 동작할 수 있다.
    const applyPerViewClass = virtual || !isDesktop;
    const cName = classNameBind([
      'product-list',
      applyPerViewClass && perView ? `per-view-${perView}` : '',
      className || '',
    ]);

    const {
      shouldObserve,
      activeVideoIndex,
      setActiveVideoIndex,
      visibleIndicesRef,
    } = useShortFormPlayback({
      shortFormPlayback,
      isVideoPlay,
      children,
    });

    const getListRender = useCallback(
      (el: React.ReactNode) => {
        if (Array.isArray(el)) {
          return el
            .map((v: React.ReactElement, i) => {
              // React element가 아닌 값들(null, undefined, 문자열, 숫자 등)을 필터링
              if (v && React.isValidElement(v)) {
                return (
                  <ObservedItem
                    key={v.key || `item-${i}`}
                    shouldObserve={shouldObserve}
                    index={i}
                    setActiveVideoIndex={setActiveVideoIndex}
                    activeVideoIndex={activeVideoIndex}
                    visibleIndices={visibleIndicesRef.current}
                    viewportOffset={viewportOffset}
                    isVideoPlay={isVideoPlay}
                  >
                    {v as React.ReactElement<VideoChildProps>}
                  </ObservedItem>
                );
              }
              return null;
            })
            .filter((item): item is React.ReactElement => item !== null);
        }
        return null;
      },
      [
        activeVideoIndex,
        isVideoPlay,
        shouldObserve,
        viewportOffset,
        setActiveVideoIndex,
        visibleIndicesRef,
      ]
    );

    const getChildrenRender = useCallback(
      () => (shortFormPlayback ? getListRender(children) : children),
      [children, getListRender, shortFormPlayback]
    );

    const mergedStyle: React.CSSProperties | undefined = virtual
      ? {
          ...style,
          ['--lp-mobile-virt-cols' as string]: String(
            Math.max(1, mobileVirtualColumnCount ?? 2)
          ),
        }
      : style;

    return (
      <S.ProductList className={cName} style={mergedStyle}>
        {virtual ? (
          children
        ) : (
          <ItemsGrid
            perView={perView}
            rowGap={36}
            colGap={1}
            xl={xl}
            ref={gridRef}
            {...gridProps}
          >
            {getChildrenRender()}
          </ItemsGrid>
        )}
      </S.ProductList>
    );
  }
);

export default memo(ProductList);
